import Text from "../../../../../../../../components/Text";
import Flex from "../../../../../../../../components/Flex";
import CircleIcon from "@mui/icons-material/Circle";
import { RightAction } from "../PatientRecords";
import { CareOptions, paginationForSideBar } from "./SummaryConstant";
import ActionableHoverNew from "../ActionableHoverNew";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import UpsertCarePlanDrawer from "../../care-plan/UpsertCarePlanDrawer";
import { usePatientCarePlanList } from "../../../../../../../../queries/care-plan/use-patient-careplan-list";
import { useParams } from "react-router-dom";
import { useState } from "react";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import { CarePlanTooltip } from "./encounterTooltips/CarePlanTooltip";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../../utils/utils";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useClinicalSummaryLink } from "../../../../../../../../queries/encounter/use-clinical-summary-link";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { ClinicalSummaryLinkRequestClinicalSummaryTypeEnum } from "../../../../../../../../services";
import { CarePlanHistorysingleline } from "./Care";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useArchiveCareplan } from "../../../../../../../../queries/care-plan/use-patient-careplan-archive";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";
import { useRecordsScroll } from "./RecordsScrollContext";

const PastCarePlan = () => {
  const carePlanDisclosure = useDisclosure();
  const deletePastCarePlanDisclosure = useDeleteDisClosure();
  const { mutateAsync: archiveCareplan } = useArchiveCareplan();

  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});
  const { mutateAsync: linkClinicalDataToAppointment } =
    useClinicalSummaryLink();
  const queryClient = useQueryClient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { patientId } = useParams();
  const { isScrolling } = useRecordsScroll();
  const {
    data: carePlanHistory,
    refetch: refetchCarePlanHistory,
    isLoading: isCarePlanHistoryLoading,
  } = usePatientCarePlanList({
    patientUuid: patientId ?? "",
    pagination: paginationForSideBar,
    archive: false,
    current: false,
    enabled: true,
  });

  const clickHandler = (v: string, idx?: number) => {
    // console.log("v - ", v);
    if (v === "add") {
      carePlanDisclosure.open();
    }
    if (v === "Edit") {
      const carePlanTransformed = {
        ...carePlanHistory?.data?.content[idx ?? 0],
        icdCodes: transformMultiSelectFrontEndVal(
          DEFAULT_VALUE_LABEL_LIST,
          carePlanHistory?.data?.content[idx ?? 0]?.icdCodes
        ),
      };
      carePlanDisclosure.open(carePlanTransformed);
    }
    // note : we are just posting care plan to note, but we are not showing care plan in any of the visit notes.
    // need to ask wheter history care plan should be add /( hit post API ) in visit note or not.
    if (v === "ExportToNote") {
      linkClinicalDataToAppointment({
        clinicalSummaryLinkRequest: {
          clinicalSummaryType:
            ClinicalSummaryLinkRequestClinicalSummaryTypeEnum.Careplan,
          recordId: carePlanHistory?.data?.content[idx ?? 0]?.uuid,
          appointmentId: appointmentid ?? "",
        },
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({
            queryKey: ["patient-careplan-list"],
          });
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }
    if (v === "Delete") {
      deletePastCarePlanDisclosure.open(
        carePlanHistory?.data?.content[idx ?? 0]?.uuid
      );
    }
  };
  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };
  function archiveHandler<T>(value: boolean, id: T) {
    return archiveCareplan({
      carePlanId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetchCarePlanHistory();
      return res;
    });
  }

  return (
    <>
      <DeleteModal
        w="380px"
        open={deletePastCarePlanDisclosure.opened}
        onClose={deletePastCarePlanDisclosure.close}
        id={deletePastCarePlanDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Past Care Plan"}
      />
      <Flex flexDirection={"column"} width={"100%"} gap={1}>
        <UpsertCarePlanDrawer
          curFormState={"add"}
          disclosureHost={carePlanDisclosure}
        />
        <Flex alignItems={"center"} gap={1} ml={1}>
          <CircleIcon sx={{ fontSize: 10 }} />
          <Text fontWeight={600} color="text.secondary">
            Past Care Plan History
          </Text>
        </Flex>
        <LoadingAndEmptyStateWrapper
          loading={isCarePlanHistoryLoading}
          isEmpty={carePlanHistory?.data?.content?.length === 0}
          emptyMessage="No Past Care Plan found"
        />
        {carePlanHistory?.data?.content?.map((it, idx: number) => {
          const isTooltipDisabled = disabledTooltips[idx] || false;
          return (
            <Flex key={idx} width={"100%"}>
              <CustomTooltip
                key={it?.uuid}
                content={<CarePlanTooltip key={idx} data={it} />}
                position="right"
                disableTooltip={isTooltipDisabled || isScrolling}
              >
                <ActionableHoverNew
                  renderAction={
                    <div
                      onMouseEnter={() => handleActionAreaToggle(idx, true)}
                      onMouseLeave={() => handleActionAreaToggle(idx, false)}
                    >
                      <RightAction
                        handleClickOption={(v) => clickHandler(v, idx)}
                        menuList={CareOptions}
                        onMenuOpen={() => handleMenuToggle(idx, true)}
                        onMenuClose={() => handleMenuToggle(idx, false)}
                      />
                    </div>
                  }
                >
                  <CarePlanHistorysingleline it={it} idx={idx} />
                </ActionableHoverNew>
              </CustomTooltip>
            </Flex>
          );
        })}
      </Flex>
    </>
  );
};

export default PastCarePlan;
